import { useMemo } from 'react'
import { Button, Dialog } from '@bloo-team/ui'
import { suggestionLabel, type TriageSuggestion } from '../../lib/signal-ai'
import { DISPOSITION_LABEL, type Disposition, type Signal } from '../../lib/signal-model'

/** The note every close from this dialog carries — the audit says where the verdict came from. */
export const APPLY_SUGGESTIONS_NOTE = 'Accepted AI suggestion'

export interface ApplySuggestionsDialogProps {
  open: boolean
  items: { signal: Signal; suggestion: TriageSuggestion }[]
  onCancel: () => void
  /** One group per disposition; undetermined suggestions are never in it. */
  onConfirm: (groups: { disposition: Disposition; uuids: string[] }[]) => void
}

/**
 * Bulk "Apply suggestions" (demo only — lib/signal-ai). Lists each selected
 * signal with the disposition it would close as; nothing is applied until
 * Confirm. Undetermined suggestions are left out and counted.
 */
export function ApplySuggestionsDialog({ open, items, onCancel, onConfirm }: ApplySuggestionsDialogProps) {
  const { apply, skipped, groups } = useMemo(() => {
    const apply = items.filter((i) => i.suggestion.disposition !== 'undetermined')
    const by = new Map<Disposition, string[]>()
    for (const i of apply) by.set(i.suggestion.disposition, [...(by.get(i.suggestion.disposition) ?? []), i.signal.uuid])
    return {
      apply,
      skipped: items.length - apply.length,
      groups: [...by.entries()].map(([disposition, uuids]) => ({ disposition, uuids })),
    }
  }, [items])

  return (
    <Dialog open={open} onClose={onCancel} title={`Apply ${apply.length} suggestion${apply.length === 1 ? '' : 's'}`}>
      <div className="hud-stack sp-form">
        <p className="sp-form-what">
          Each signal below closes with its suggested disposition and the note “{APPLY_SUGGESTIONS_NOTE}”.
        </p>
        {apply.length ? (
          <ul className="sg-apply-list">
            {apply.map(({ signal, suggestion }) => (
              <li key={signal.uuid} className="sg-apply-row">
                <span className="sg-apply-name">{signal.detectionName || signal.uuid}</span>
                <span className="mono">→ {suggestionLabel(suggestion)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="q-facet-note">No selected signal has a suggestion to apply.</p>
        )}
        {groups.length > 1 ? (
          <p className="hud-unit">
            {groups.map((g) => `${g.uuids.length} ${DISPOSITION_LABEL[g.disposition].toLowerCase()}`).join(' · ')}
          </p>
        ) : null}
        {skipped ? (
          <p className="hud-unit" role="status">
            {skipped} undetermined — left open
          </p>
        ) : null}
        <div className="hud-row sp-form-actions">
          <Button variant="ghost" size="sm" tone="neutral" onPress={onCancel}>
            Cancel
          </Button>
          <Button variant="solid" size="sm" isDisabled={apply.length === 0} onPress={() => onConfirm(groups)}>
            Close {apply.length} signal{apply.length === 1 ? '' : 's'}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
